"use client";

import * as React from "react";
import { useMediaQuery } from "@/lib/hooks/ui/use-media-query";
import { useGlobalDrawerStack } from "@wtc/ui";
import { useSelectRecents } from "./useSelectRecents";
import { useResponsiveSelectState } from "./hooks/useResponsiveSelectState";
import { useResponsiveSelectHandlers } from "./hooks/useResponsiveSelectHandlers";
import { useResponsiveSelectPosition } from "./hooks/useResponsiveSelectPosition";
import { ResponsiveSelectVariant } from "./components/ResponsiveSelectVariant";
import type { SelectOption } from "./types";
import type { ResponsiveCommandSelectProps } from "@/lib/types/select-components";

export function ResponsiveCommandSelect({
  value,
  onValueChange,
  options,
  placeholder = "Select...",
  disabled = false,
  searchValue,
  onSearchChange,
  isSearching = false,
  className,
  title = "Select Option",
  description,
  searchPlaceholder = "Search options...",
  highZIndex = false,
  enableRecents = false,
  recentKey,
  recentMax = 8,
  onRecordRecent,
  onLoadMore,
  hasMore,
  isLoadingMore,
  customOptionRenderer,
  centerOnScreen = false,
  expandInline = true,
  expandInlineVariant = "floating",
  maxDropdownWidth,
  onOpenChange: onOpenChangeProp,
}: ResponsiveCommandSelectProps) {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const dropdownRef = React.useRef<HTMLDivElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const triggerRef = React.useRef<HTMLButtonElement>(null);

  const isDesktop = useMediaQuery("(min-width: 768px)");
  const { openDrawer, closeDrawer } = useGlobalDrawerStack();

  // State management
  const {
    state,
    setOpen,
    setInternalSearch,
    setTriggerWidth,
    setDropdownPosition,
    setRecentsNonce,
    setTooltipContainer,
  } = useResponsiveSelectState(onOpenChangeProp);

  const { open, internalSearch, triggerWidth, dropdownPosition, recentsNonce, tooltipContainer } = state;

  // Position calculation for floating variant
  const calculatedPosition = useResponsiveSelectPosition({
    expandInline,
    expandInlineVariant,
    open,
    triggerRef,
    maxDropdownWidth,
  });

  // Effective search value
  const effectiveSearch = onSearchChange ? searchValue || "" : internalSearch;

  // Recents
  const recentOptions = useSelectRecents({
    enableRecents,
    recentKey,
    options,
    effectiveSearch,
    recentsNonce,
  });

  // Selected option
  const selected = React.useMemo(() => {
    const inOptions = options.find((o) => o.value === value);
    if (inOptions) return inOptions;
    const inRecents = recentOptions.find((o) => o.value === value);
    return inRecents || null;
  }, [options, recentOptions, value]);

  // Drawer ID
  const drawerId = React.useMemo(() => {
    return `responsive-command-select-${Math.random().toString(36).substr(2, 9)}`;
  }, []);

  // Event handlers
  const { handleSelect, handleClearRecents } = useResponsiveSelectHandlers({
    options,
    recentOptions,
    onValueChange,
    onSearchChange,
    enableRecents,
    recentKey,
    recentMax,
    onRecordRecent,
    drawerId,
    closeDrawer,
    setOpen,
    setRecentsNonce,
  });

  // Effects
  React.useEffect(() => {
    if (open && inputRef.current) {
      const timer = setTimeout(() => {
        inputRef.current?.focus();
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [open, searchValue]);

  React.useEffect(() => {
    if (isDesktop && open && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setTriggerWidth(rect.width);
    }
  }, [isDesktop, open, setTriggerWidth]);

  React.useLayoutEffect(() => {
    if (!expandInline) return;
    const el = triggerRef.current;
    if (!el) return;
    const dialog = el.closest("[role=\"dialog\"]") as HTMLElement | null;
    setTooltipContainer(dialog);
  }, [expandInline, open, setTooltipContainer]);

  React.useEffect(() => {
    if (!expandInline || expandInlineVariant !== "floating" || !open) return;
    const handlePointerDown = (e: PointerEvent) => {
      const target = e.target as Node;
      if (
        containerRef.current &&
        !containerRef.current.contains(target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("pointerdown", handlePointerDown);
    return () => document.removeEventListener("pointerdown", handlePointerDown);
  }, [expandInline, expandInlineVariant, open, setOpen]);

  React.useEffect(() => {
    if (!expandInline || expandInlineVariant !== "floating" || !open) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener("keydown", handleKeyDown, true);
    return () => document.removeEventListener("keydown", handleKeyDown, true);
  }, [expandInline, expandInlineVariant, open, setOpen]);

  // Computed values
  const hasRecents = !!(enableRecents && recentKey && recentOptions.length > 0);
  const recentValueSet = React.useMemo(
    () => new Set(recentOptions.map((o) => o.value)),
    [recentOptions]
  );

  const listProps = {
    inputRef,
    searchPlaceholder,
    effectiveSearch,
    onSearchChange: onSearchChange ?? setInternalSearch,
    isSearching,
    hasRecents,
    recentOptions,
    recentValueSet,
    options,
    value,
    onSelect: handleSelect,
    onLoadMore,
    hasMore,
    isLoadingMore,
    enableRecents,
    recentKey,
    onClearRecents: handleClearRecents,
    customOptionRenderer,
  };

  // Determine variant
  let variant: "floating" | "inline-replace" | "inline-floating" | "desktop" | "mobile" | "center-screen" = "mobile";
  if (expandInline && expandInlineVariant === "floating") {
    variant = "inline-floating";
  } else if (expandInline) {
    variant = "inline-replace";
  } else if (centerOnScreen) {
    variant = "center-screen";
  } else if (isDesktop) {
    variant = "desktop";
  }

  return (
    <ResponsiveSelectVariant
      variant={variant}
      selected={selected}
      placeholder={placeholder}
      disabled={disabled}
      className={className}
      triggerRef={triggerRef}
      containerRef={containerRef}
      dropdownRef={dropdownRef}
      open={open}
      onOpenChange={setOpen}
      dropdownPosition={calculatedPosition || dropdownPosition}
      title={title}
      description={description}
      listProps={listProps}
      triggerWidth={triggerWidth}
      highZIndex={highZIndex}
      drawerId={drawerId}
      tooltipContainer={tooltipContainer}
      isDesktop={isDesktop}
      onOpenChangeProp={onOpenChangeProp}
    />
  );
}
